import * as types from '~/store/mutation-types'
import { COOKIE_CATEGORY } from 'enums/cmp'
import { isArray, isString } from '@/plugins/helper'

function wasConsentOptedOut(newConsent, oldConsent) {
  if (!oldConsent || !newConsent) return false

  /**
   * We do not reload a page in case if rejected category is relevant
   * to IAB Vendors
   */
  const oldConsentFormatted = oldConsent.replace(COOKIE_CATEGORY.STACK_42, '')
  const newConsentFormatted = newConsent.replace(COOKIE_CATEGORY.STACK_42, '')

  return oldConsentFormatted
    .split(',')
    .some(group => !newConsentFormatted.includes(group))
}

export const state = () => ({
  cmpActiveGroups: COOKIE_CATEGORY.STRICTLY_NECESSARY,
  previousCmpActiveGroups: COOKIE_CATEGORY.STRICTLY_NECESSARY,
  executeHandlersOnConsentChange: []
})

export const getters = {
  cmpActiveGroups: state => state.cmpActiveGroups,
  previousCmpActiveGroups: state => state.previousCmpActiveGroups,
  executeHandlersOnConsentChange: state => state.executeHandlersOnConsentChange
}

export const actions = {
  updateConsentActiveGroups({ dispatch, commit }, groupsArr) {
    if (!isArray(groupsArr)) return

    const groupsString =
      groupsArr.join(',') || COOKIE_CATEGORY.STRICTLY_NECESSARY

    commit(`cmp/${types.UPDATE_CMP_CONSENT_ACTIVE_GROUPS}`, groupsString, {
      root: true
    })
    dispatch(`cmp/executeHandlersOnConsentChange`, null, {
      root: true
    })
  },
  executeHandlersOnConsentChange({ commit, getters }) {
    const wasPreviouslyAcceptedCookieRejected = wasConsentOptedOut(
      getters.cmpActiveGroups,
      getters.previousCmpActiveGroups
    )

    /**
     * We purposefully reload a page when there was an opt-out action for any
     * cookie category. The referrer is going to be lost in this case due to the
     * programmatic reload.
     * (Approved by the client)
     */
    if (wasPreviouslyAcceptedCookieRejected) {
      window.location.reload()
      return
    }

    const {
      handlersDataToExecute,
      handlersDataToKeep
    } = getters.executeHandlersOnConsentChange.reduce(
      (acc, cmpHandler) => {
        if (cmpHandler.isConsentGiven()) {
          acc.handlersDataToExecute.push(cmpHandler)
        } else {
          acc.handlersDataToKeep.push(cmpHandler)
        }

        return acc
      },
      {
        handlersDataToExecute: [],
        handlersDataToKeep: []
      }
    )

    handlersDataToExecute.forEach(cmpHandler => cmpHandler.execute())

    commit(
      `cmp/${types.UPDATE_HANDLERS_TO_EXECUTE_ON_CONSENT_CHANGE}`,
      handlersDataToKeep,
      { root: true }
    )
  },
  registerHandlerThatRequiresConsent({ commit }, cmpHandler) {
    if (cmpHandler.isConsentGiven()) {
      cmpHandler.execute()
      return
    }

    commit(
      `cmp/${types.REGISTER_HANDLER_TO_EXECUTE_ON_CONSENT_CHANGE}`,
      cmpHandler,
      { root: true }
    )
  }
}

export const mutations = {
  [types.REGISTER_HANDLER_TO_EXECUTE_ON_CONSENT_CHANGE](state, cmpHandler) {
    state.executeHandlersOnConsentChange.push(cmpHandler)
  },
  [types.UPDATE_HANDLERS_TO_EXECUTE_ON_CONSENT_CHANGE](state, handlersData) {
    state.executeHandlersOnConsentChange = handlersData
  },
  [types.REMOVE_LOCAL_HANDLERS_ON_ROUTE_CHANGE](state) {
    state.executeHandlersOnConsentChange = state.executeHandlersOnConsentChange.filter(
      cmpHandler => !cmpHandler.isLocal
    )
  },
  [types.REMOVE_HANDLERS_FOR_ENTITY_TYPE](state, entityType) {
    state.executeHandlersOnConsentChange = state.executeHandlersOnConsentChange.filter(
      cmpHandler => cmpHandler.entityType !== entityType
    )
  },
  [types.SET_CMP_CONSENT_ACTIVE_GROUPS_FROM_COOKIE](state, cookieValue) {
    if (!cookieValue || !isString(cookieValue)) return

    let parsed
    try {
      parsed = JSON.parse(cookieValue)
    } catch (e) {
      return
    }

    // `categories` in the example is a JSON-encoded array string, but be tolerant.
    let categories = parsed?.categories
    if (!categories) return

    if (isString(categories)) {
      try {
        categories = JSON.parse(categories)
      } catch (e) {
        return
      }
    }

    if (!isArray(categories)) return

    const activeGroupsArr = Array.from(
      new Set(
        [COOKIE_CATEGORY.STRICTLY_NECESSARY, ...categories].filter(Boolean)
      )
    )

    if (!activeGroupsArr.length) return

    state.cmpActiveGroups = activeGroupsArr.join(',')
    state.previousCmpActiveGroups = state.cmpActiveGroups
  },
  [types.UPDATE_CMP_CONSENT_ACTIVE_GROUPS](state, value) {
    state.previousCmpActiveGroups = state.cmpActiveGroups
    state.cmpActiveGroups = value
  }
}
